/**
 * SearchableAccountPicker — replaces the plain <select> element for
 * investor-account dropdowns in the Commitment Transfer form.
 *
 * • Text input with placeholder — user types to filter the list by
 *   account name or PAN.
 * • Dropdown list appears on focus and stays open while the user types.
 * • Each option can carry a small kyc badge and/or a "holds in fund"
 *   badge — passed via the `renderMeta` prop.
 * • Excluded ids (e.g. the currently-selected transferor when picking a
 *   transferee) are hidden from the list.
 * • Fully keyboard-navigable (Arrow keys + Enter + Escape).
 *
 * Kept as a self-contained component so it can be re-used on other
 * long dropdowns (e.g. Fund selector once we have 50+ funds).
 */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';

export default function SearchableAccountPicker({
  value,
  onChange,
  accounts,                    // [{ id, account_name, pan, ... }]
  placeholder = 'Search account by name or PAN…',
  disabled = false,
  excludeIds = [],             // hide these ids from the list
  filterFn,                    // optional (acc) => bool → hard filter
  disableFn,                   // optional (acc) => bool → grey-out only
  renderMeta,                  // optional (acc) => ReactNode  (row badges)
  testId,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef(null);
  const listRef = useRef(null);

  // ------------------------------------------------------------------
  // Derived: the label shown when nothing is being typed.
  // ------------------------------------------------------------------
  const selected = useMemo(
    () => accounts.find(a => a.id === value) || null,
    [accounts, value],
  );

  // ------------------------------------------------------------------
  // Filter + sort. Selected item always appears in list too (so the
  // user can see what's currently chosen when they re-open).
  // ------------------------------------------------------------------
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const excludeSet = new Set(excludeIds);
    const passes = (a) => {
      if (excludeSet.has(a.id)) return false;
      if (filterFn && !filterFn(a)) return false;
      if (!q) return true;
      return (
        (a.account_name || '').toLowerCase().includes(q)
        || (a.pan || '').toLowerCase().includes(q)
      );
    };
    return accounts.filter(passes);
  }, [accounts, query, excludeIds, filterFn]);

  // ------------------------------------------------------------------
  // Close dropdown when clicking outside.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  // ------------------------------------------------------------------
  // Keyboard handling.
  // ------------------------------------------------------------------
  const commit = (acc) => {
    if (!acc) return;
    if (disableFn && disableFn(acc)) return;    // greyed-out → not selectable
    onChange(acc.id);
    setQuery('');
    setOpen(false);
  };
  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') { setOpen(true); e.preventDefault(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      setActiveIdx(i => Math.min(i + 1, visible.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setActiveIdx(i => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter') {
      commit(visible[activeIdx]);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Reset active row when the filter changes.
  useEffect(() => { setActiveIdx(0); }, [query, visible.length]);

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      <input
        type="text"
        disabled={disabled}
        value={open ? query : (selected?.account_name || '')}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { if (!disabled) setOpen(true); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={
          'w-full text-sm border rounded-lg px-3 py-2 bg-white '
          + (disabled ? 'disabled:bg-gray-50 disabled:cursor-not-allowed' : '')
        }
        data-testid={testId ? `${testId}-input` : undefined}
        autoComplete="off"
      />
      {selected && !open && !disabled && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 text-sm"
          aria-label="Clear selection"
          data-testid={testId ? `${testId}-clear` : undefined}
        >
          ×
        </button>
      )}

      {open && !disabled && (
        <div
          ref={listRef}
          className="absolute z-40 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border bg-white shadow-lg"
          data-testid={testId ? `${testId}-list` : undefined}
        >
          {visible.length === 0 ? (
            <div className="px-3 py-4 text-xs text-gray-500 italic">
              No accounts match the search.
            </div>
          ) : (
            visible.map((a, i) => {
              const isSelected = a.id === value;
              const isDimmed = disableFn ? disableFn(a) : false;
              return (
                <button
                  type="button"
                  key={a.id}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => commit(a)}
                  disabled={isDimmed}
                  className={
                    'w-full text-left px-3 py-2 flex items-center justify-between gap-2 border-b last:border-b-0 '
                    + (isDimmed ? 'opacity-50 cursor-not-allowed ' : 'hover:bg-blue-50 cursor-pointer ')
                    + (i === activeIdx ? 'bg-blue-50 ' : '')
                    + (isSelected ? 'font-semibold text-blue-700' : 'text-gray-800')
                  }
                  data-testid={testId ? `${testId}-option-${a.id}` : undefined}
                >
                  <span className="flex-1 min-w-0">
                    <span className="text-sm truncate block">
                      {a.account_name || '—'}
                    </span>
                    {a.pan ? (
                      <span className="text-[11px] text-gray-500 font-mono">
                        PAN: {a.pan}
                      </span>
                    ) : null}
                  </span>
                  {renderMeta ? (
                    <span className="flex-shrink-0">{renderMeta(a)}</span>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
